import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import { ReactComponent as ApMark } from '@rocco/icons/svg/archipro-receipt-mark.svg';
import { useUserLoggedIn } from '@modules/user/hook/use-user';
import type { GetMatchedStyleImage } from '~/modules/get-matched/api/get-matched-style-images.server';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useGetBuildHubsList } from '~/ui-rocco/hooks/use-build-hubs';
import {
    getMatchedEntryPointAtom,
    getMatchedModalOpenAtom,
} from '~/state/ui-state-get-matched-modal';
import { useGetMatchedModalUrl } from './use-get-matched-modal-url';

import { useQuestionnaireTracking } from '../questionnaire';
import { GET_MATCHED_EVENT_NAMES } from './get-matched-events';
import { briefConfirmationHref } from './requests/request-routes';
import { useLastBrief } from './use-last-brief';
import { GetMatchedQuestionnaire } from './GetMatchedQuestionnaire';
import {
    buildProductDefinition,
    buildProductOutcome,
} from './definitions/product';
import {
    PROFESSIONAL_INITIAL_ANSWERS,
    buildProfessionalDefinition,
    buildProfessionalOutcome,
} from './definitions/professional';
import productPane from './images/get-matched-product.jpg';
import professionalPane from './images/get-matched-professional.jpg';

export type GetMatchedEnquiryType = 'professional' | 'product';

export interface GetMatchedHostProps {
    /**
     * Which brief this surface starts. Project-originated entries use
     * `professional`: a match is with a practice, and matching someone to
     * another project serves nobody.
     */
    enquiryType?: GetMatchedEnquiryType;
    /** CMS photography for the style tiles. Professional flow only. */
    styleImages?: GetMatchedStyleImage[];
}

/**
 * Mounts Get Matched on a page and holds it against the shared open atom.
 *
 * Rendering more than one on a page is safe for behaviour — nothing here owns
 * state beyond the atom, and the questionnaire keeps no cross-visit storage, so
 * duplicates simply open together — but it is **not** safe for reporting. Each
 * mount runs its own questionnaire, so each reports its own funnel, and two
 * mounts double every event from the open to the submit.
 *
 * One per page, therefore. Where a page has more than one place to put the
 * brief, the two must be alternatives rather than both rendered: the product
 * detail page carries a closing band in each of its two section orders and
 * picks between them, rather than rendering both and hiding one with CSS.
 */
export const GetMatchedHost = ({
    enquiryType = 'professional',
    styleImages,
}: GetMatchedHostProps) => {
    const [open, setOpen] = useAtom(getMatchedModalOpenAtom);
    // Back closes the brief and a shared ?modal=get-matched link opens it.
    useGetMatchedModalUrl();
    const { isLoggedIn, user } = useUserLoggedIn();

    // Which prompt started this, on every event rather than only the open —
    // that is what lets a submitted brief be traced back to the control that
    // won it, and a drop-off curve be read per prompt.
    const entryPoint = useAtomValue(getMatchedEntryPointAtom);
    const trackingContext = useMemo(
        () => ({ entryPoint: entryPoint ?? undefined }),
        [entryPoint],
    );
    const trackEvent = useQuestionnaireTracking(
        GET_MATCHED_EVENT_NAMES,
        trackingContext,
    );

    // A brief is a need within a project, so the flow opens by asking which one
    // — but only for a reader who has any.
    //
    // Asked for when this mounts rather than when the brief opens. The opening
    // step depends on the answer, and a flow that opens first and learns second
    // shows the wrong step and then corrects itself in front of the reader. By
    // the time anyone clicks, the page has usually had this for a while.
    const buildHubEnabled = useFeature('buildhub');
    const canHaveProjects = isLoggedIn && buildHubEnabled;
    const [projects, projectsLoading, fetchProjects] = useGetBuildHubsList();

    // Asked once, tracked in a ref rather than derived from the result. A
    // failed request leaves the list undefined, so a guard that reads the list
    // cannot tell "not asked yet" from "asked and got nothing" — and re-fires
    // forever. The fetch callback is rebuilt on every fetcher tick too, so it
    // cannot hold the effect still on its own.
    const requestedProjects = useRef(false);
    useEffect(() => {
        if (!canHaveProjects || requestedProjects.current) return;
        requestedProjects.current = true;
        fetchProjects();
    }, [canHaveProjects, fetchProjects]);

    // Whether each answer is in, either way. A reader without the feature has
    // theirs immediately: they have no projects.
    const sawProjectsLoading = useRef(false);
    if (projectsLoading) sawProjectsLoading.current = true;
    const projectsAnswered =
        !canHaveProjects ||
        projects !== undefined ||
        (sawProjectsLoading.current && !projectsLoading);

    // Who they said they were last time. They do not become someone else
    // between briefs, so it is the one answer worth not asking twice. Asked for
    // at mount alongside the projects, and waited on alongside them: seeding it
    // after the step is on screen would move the selection under the reader.
    const {
        brief: lastBrief,
        loading: briefLoading,
        settled: briefSettled,
    } = useLastBrief(isLoggedIn);

    const [waitedLongEnough, setWaitedLongEnough] = useState(false);

    // A lookup that never comes back must not hold the brief shut. Timed from
    // when the asking starts rather than from when the brief opens: a guest
    // signs in first, so by the time these are even requested the brief has
    // been open for longer than any ceiling worth having.
    useEffect(() => {
        if (!isLoggedIn) return;
        const timer = setTimeout(() => setWaitedLongEnough(true), 1500);
        return () => clearTimeout(timer);
    }, [isLoggedIn]);

    // The ceiling is for a request that never starts or never returns — it must
    // not fire over one that is simply still travelling. Giving up on an
    // in-flight lookup is how the opening question ends up missing entirely.
    const inFlight = projectsLoading || briefLoading;
    const released =
        (projectsAnswered && briefSettled) || (waitedLongEnough && !inFlight);

    // Taken once and kept. A list that arrives after the flow has committed to
    // its shape is ignored rather than folded in: inserting a step ahead of a
    // reader who is already answering moves them off a question they have read.
    // Nothing is captured while they are still a guest — signing in is what
    // makes them someone who might have projects, and it happens inside here.
    const [settled, setSettled] = useState<{
        projects: typeof projects;
        userType: string | undefined;
    }>();
    if (isLoggedIn && released && !settled) {
        setSettled({ projects, userType: lastBrief?.userType ?? undefined });
    }

    // Positive, not "we are not currently waiting". `settling` was false both
    // before the lookup was asked for and after it answered, and a reader who
    // signs in inside the flow passes through the first of those: the wall
    // clears a beat before the session is observed here, so nothing had been
    // requested yet and nothing was holding the door.
    const shapeSettled = settled !== undefined;

    const deferredAnswers = useMemo(
        () => (settled?.userType ? { userType: settled.userType } : undefined),
        [settled?.userType],
    );

    // The requests screen is behind its own flag — released separately from the
    // questionnaire, because the first release answers a brief by email alone. So the
    // reference is only a way back where the reader can actually follow it. Off, it stays
    // a handle they can quote rather than a link into a page that refuses them.
    const canOpenRequests = useFeature('get_matched_requests_online');

    const isProduct = enquiryType === 'product';

    const definition = useMemo(
        () =>
            isProduct
                ? buildProductDefinition({
                      paneImage: productPane,
                      projects: settled?.projects,
                  })
                : buildProfessionalDefinition({
                      paneImage: professionalPane,
                      projects: settled?.projects,
                      styleImages,
                  }),
        [isProduct, settled, styleImages],
    );

    // The reader may still be a guest here, and that shape carries no name.
    const firstName =
        user && 'FirstName' in user ? (user.FirstName ?? undefined) : undefined;

    const outcome = useMemo(
        () =>
            isProduct
                ? buildProductOutcome()
                : buildProfessionalOutcome(firstName),
        [isProduct, firstName],
    );

    if (!open) return null;

    return (
        <GetMatchedQuestionnaire
            buildReferenceHref={
                canOpenRequests ? briefConfirmationHref : undefined
            }
            definition={definition}
            deferredAnswers={deferredAnswers}
            initialAnswers={
                isProduct ? undefined : PROFESSIONAL_INITIAL_ANSWERS
            }
            logo={
                <ApMark className="h-11 w-14.5 text-[#181818] md:h-17.75 md:w-23.5" />
            }
            onEvent={trackEvent}
            onOpenChange={setOpen}
            open={open}
            outcome={outcome}
            // Which question opens the flow depends on this answer, so the
            // questions wait for it. Unmounting instead would take the auth
            // step down with them: a guest only becomes someone who might have
            // projects by signing in, which happens inside the flow.
            shapeSettled={shapeSettled}
        />
    );
};
